import React from 'react';
import styles from './Header.module.css';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useHeaderFooter } from '../../context/HeaderFooterContext';

function Header() {
  const { headerElement, menus, loading } = useHeaderFooter();
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Show loading or return null while data is being fetched
  if (loading) {
    return null; // or a loading spinner
  }

  const layout = headerElement?.layout ?? 0;
  const bgColor = headerElement?.bgColorCode || '#ffffff';
  const color = headerElement?.colorCode || '#000000';
  const logoUrl = headerElement?.imageUrl || '';
  const logoLink = headerElement?.logoUrl;
  

  const rowOneMenus = menus
    .filter(m => m.menuType === 1)
    .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
  const rowTwoMenus = menus
    .filter(m => m.menuType === 2)
    .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));

  const isLogoLeft = layout === 0 || layout === 4;
  const menuAlignRight = isLogoLeft; // when logo is left, menus align right; otherwise align left

  const getBasePath = () => {
    const parts = (location.pathname || '').split('/').filter(Boolean);
    if (parts[0] === 'Division' && parts[2] === 'Department' && parts[1] && parts[3]) {
      return `/Division/${parts[1]}/Department/${parts[3]}`;
    }
    if (parts[0] === 'Division' && parts[1]) {
      return `/Division/${parts[1]}`;
    }
    // For any other path (including incomplete paths like /Division or /page), default to root
    return '/';
  };

  const getLogoNavigationUrl = () => {
    return logoLink && logoLink.trim() !== '' ? logoLink : getBasePath();
  };

  const renderMenuLink = (menu) => {
    const label = menu.newTitle || menu.title;
    const href = menu.url || '#';
    const isExternal = /^https?:\/\//i.test(href);
    if (isExternal) {
      return (
        <a
          key={menu.id}
          href={href}
          className={styles.menuLink}
          style={{ color }}
          target="_blank"
          rel="noreferrer"
        >
          {label}
        </a>
      );
    }
    // Resolve relative links against the department root path: /Division/:div/Department/:dept
    let to = href;
    if (!href.startsWith('/')) {
      const basePath = getBasePath();
      // Ensure no double slashes by removing trailing slash from basePath if it exists
      const cleanBasePath = basePath.endsWith('/') && basePath !== '/' ? basePath.slice(0, -1) : basePath;
      to = basePath === '/' ? `/${href}` : `${cleanBasePath}/${href}`;
    }
    return (
      <Link
        key={menu.id}
        to={to}
        className={styles.menuLink}
        style={{ color }}
      >
        {label}
      </Link>
    );
  };

  // Centered layout for layout === 2
  if (layout === 2) {
    return (
      <header className={styles.headerRoot}>
        <div className={styles.bar} style={{ background: bgColor, color }}>
          <div className={styles.container}>
            <div className={styles.centerWrap}>
              <Link to={getLogoNavigationUrl()} className={styles.logoWrap} style={{ textDecoration: 'none' }} aria-label="Go to homepage">
                {logoUrl ? (
                  <img className={styles.logoImg} src={logoUrl} alt="Logo" />
                ) : null}
              </Link>
              <button className={styles.toggler} aria-label="Toggle navigation" type="button" onClick={() => setIsMobileOpen(v => !v)}>☰</button>
              <nav aria-label="Primary Navigation">
                <div className={styles.centerMenuRow}>
                  {rowOneMenus.map(renderMenuLink)}
                </div>
              </nav>
            </div>
            {/* Mobile menu */}
            <div className={`${styles.mobileMenu} ${isMobileOpen ? 'open' : ''}`} style={{ display: isMobileOpen ? 'block' : 'none', background: bgColor, color }}>
              <div className={styles.mobileMenuInner}>
                <div className={styles.mobileMenuHeader}>
                  <div className={styles.mobileTitle}>Menu</div>
                </div>
                <div className={styles.mobileSingle}>
                  {rowOneMenus.map(menu => {
                    const href = menu.url || '#';
                    const isExternal = /^https?:\/\//i.test(href);
                    let to = href;
                    if (!href.startsWith('/')) {
                      const basePath = getBasePath();
                      to = basePath === '/' ? `/${href}` : `${basePath}/${href}`;
                    }
                    return isExternal ? (
                      <a key={`m2-${menu.id}`} href={href} className={styles.mobileLink} style={{ color }} target="_blank" rel="noreferrer" onClick={() => setIsMobileOpen(false)}>
                        {menu.newTitle || menu.title}
                      </a>
                    ) : (
                      <Link key={`m2-${menu.id}`} to={to} className={styles.mobileLink} style={{ color }} onClick={() => setIsMobileOpen(false)}>
                        {menu.newTitle || menu.title}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
    );
  }

  // Default: left/right layouts
  return (
    <header className={styles.headerRoot}>
      <div className={styles.bar} style={{ background: bgColor, color }}>
        <div className={styles.container}>
          <div className={styles.row} style={{ flexDirection: isLogoLeft ? 'row' : 'row-reverse' }}>
            {/* Logo */}
            <Link to={getLogoNavigationUrl()} className={styles.logoWrap} style={{ textDecoration: 'none' }} aria-label="Go to homepage">
              {logoUrl ? (
                <img className={styles.logoImg} src={logoUrl} alt="Logo" />
              ) : null}
            </Link>
            {/* Menus */}
            <nav className={styles.menusWrap} aria-label="Primary Navigation" style={{ alignItems: menuAlignRight ? 'flex-end' : 'flex-start' }}>
              <div className={styles.menuRow} style={{ justifyContent: menuAlignRight ? 'flex-end' : 'flex-start' }}>
                {rowOneMenus.map(renderMenuLink)}
              </div>
              {(layout !== 0 && layout !== 1) ? (
                <div className={styles.menuRow} style={{ justifyContent: menuAlignRight ? 'flex-end' : 'flex-start' }}>
                  {rowTwoMenus.map(renderMenuLink)}
                </div>
              ) : null}
            </nav>
            <button className={`${styles.toggler} ${menuAlignRight ? '' : styles.togglerRight}`} aria-label="Toggle navigation" type="button" onClick={() => setIsMobileOpen(v => !v)}>☰</button>
          </div>
          {/* Mobile menu */}
          <div className={`${styles.mobileMenu} ${isMobileOpen ? 'open' : ''}`} style={{ display: isMobileOpen ? 'block' : 'none', background: bgColor, color }}>
            <div className={styles.mobileMenuInner}>
              <div className={styles.mobileMenuHeader}>
                <div className={styles.mobileTitle}>Menu</div>
              </div>
              {((layout !== 0 && layout !== 1) && rowTwoMenus.length > 0) ? (
                <div className={styles.mobileCols}>
                  <div className={styles.mobileCol}>
                    {rowOneMenus.map(menu => {
                      const href = menu.url || '#';
                      const isExternal = /^https?:\/\//i.test(href);
                      let to = href;
                      if (!href.startsWith('/')) {
                        const basePath = getBasePath();
                        to = basePath === '/' ? `/${href}` : `${basePath}/${href}`;
                      }
                      return isExternal ? (
                        <a key={`m1-${menu.id}`} href={href} className={styles.mobileLink} style={{ color }} target="_blank" rel="noreferrer" onClick={() => setIsMobileOpen(false)}>
                          {menu.newTitle || menu.title}
                        </a>
                      ) : (
                        <Link key={`m1-${menu.id}`} to={to} className={styles.mobileLink} style={{ color }} onClick={() => setIsMobileOpen(false)}>
                          {menu.newTitle || menu.title}
                        </Link>
                      );
                    })}
                  </div>
                  <div className={styles.mobileCol}>
                    {rowTwoMenus.map(menu => {
                      const href = menu.url || '#';
                      const isExternal = /^https?:\/\//i.test(href);
                      let to = href;
                      if (!href.startsWith('/')) {
                        const basePath = getBasePath();
                        to = basePath === '/' ? `/${href}` : `${basePath}/${href}`;
                      }
                      return isExternal ? (
                        <a key={`m2-${menu.id}`} href={href} className={styles.mobileLink} style={{ color }} target="_blank" rel="noreferrer" onClick={() => setIsMobileOpen(false)}>
                          {menu.newTitle || menu.title}
                        </a>
                      ) : (
                        <Link key={`m2-${menu.id}`} to={to} className={styles.mobileLink} style={{ color }} onClick={() => setIsMobileOpen(false)}>
                          {menu.newTitle || menu.title}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className={styles.mobileSingle}>
                  {(rowOneMenus.length ? rowOneMenus : rowTwoMenus).map(menu => {
                    const href = menu.url || '#';
                    const isExternal = /^https?:\/\//i.test(href);
                    let to = href;
                    if (!href.startsWith('/')) {
                      const basePath = getBasePath();
                      to = basePath === '/' ? `/${href}` : `${basePath}/${href}`;
                    }
                    return isExternal ? (
                      <a key={`ms-${menu.id}`} href={href} className={styles.mobileLink} style={{ color }} target="_blank" rel="noreferrer" onClick={() => setIsMobileOpen(false)}>
                        {menu.newTitle || menu.title}
                      </a>
                    ) : (
                      <Link key={`ms-${menu.id}`} to={to} className={styles.mobileLink} style={{ color }} onClick={() => setIsMobileOpen(false)}>
                        {menu.newTitle || menu.title}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
